import { type TilePos } from './geometry.js';

/**
 * Walkability grid for one map. Built once from the published map (walls + collision layers)
 * and shared, read-only, by movement validation and pathfinding.
 */
export class CollisionGrid {
  readonly width: number;
  readonly height: number;
  private readonly blocked: Uint8Array;

  constructor(width: number, height: number, blocked?: Uint8Array) {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
      throw new RangeError(`Invalid grid size ${width}x${height}`);
    }
    if (blocked && blocked.length !== width * height) {
      throw new RangeError(
        `Blocked mask length ${blocked.length} does not match ${width}x${height}`,
      );
    }
    this.width = width;
    this.height = height;
    this.blocked = blocked ?? new Uint8Array(width * height);
  }

  /**
   * Builds a grid from ASCII rows, mainly for tests and fixtures.
   * `#` is blocked; any other character is walkable.
   */
  static fromRows(rows: readonly string[]): CollisionGrid {
    const height = rows.length;
    const width = rows[0]?.length ?? 0;
    const grid = new CollisionGrid(width, height);
    rows.forEach((row, y) => {
      if (row.length !== width)
        throw new RangeError(`Row ${y} has length ${row.length}, expected ${width}`);
      for (let x = 0; x < width; x++) {
        if (row[x] === '#') grid.setBlocked({ x, y }, true);
      }
    });
    return grid;
  }

  isInside(pos: TilePos): boolean {
    return (
      Number.isInteger(pos.x) &&
      Number.isInteger(pos.y) &&
      pos.x >= 0 &&
      pos.y >= 0 &&
      pos.x < this.width &&
      pos.y < this.height
    );
  }

  /** A tile is walkable when it is inside the map and not blocked. */
  isWalkable(pos: TilePos): boolean {
    return this.isInside(pos) && this.blocked[this.index(pos)] === 0;
  }

  setBlocked(pos: TilePos, value: boolean): void {
    if (!this.isInside(pos)) throw new RangeError(`Tile (${pos.x},${pos.y}) is outside the grid`);
    this.blocked[this.index(pos)] = value ? 1 : 0;
  }

  index(pos: TilePos): number {
    return pos.y * this.width + pos.x;
  }

  fromIndex(index: number): TilePos {
    return { x: index % this.width, y: Math.floor(index / this.width) };
  }
}
